// === Settings UI ===
// Settings panel, password flows, new/import session modals
// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- Historical migration exception; see .devguide/project.md.
// @ts-nocheck

import { escapeHtml, generateUUID, timeAgo, buildUpdateStatusHtml } from '../utils';
import { onWsEvent, waitForWsEvent, clearSettingsPanelSubscriptions, registerSettingsPanelHandler } from '../ws/events';
import { showToast } from './toast';
import { send } from '../ws/connection';
import { normalizeAgent } from '../session/cache';
import { normalizeComparablePath, getPathLeaf, formatClaudeImportGroupPath } from './sidebar';
import {
  buildCodexProviderSettingsState,
  buildCodexSaveConfig,
} from './codex-provider-settings-model';
import { renderCodexProviderSettings } from './codex-provider-settings';
import {
  AGENT_LABELS,
  THEME_LABELS,
  THEME_STORAGE_KEY,
  DEFAULT_THEME,
  REMEMBERED_PASSWORD_STORAGE_KEY,
} from '../constants';

const $ = (sel) => document.querySelector(sel);

let _getLoginOverlay = null;
let _getApp = null;
let _getLoginError = null;
let _getRememberPw = null;
let _getLoginPassword = null;
let _restoreInitialSession = null;
let _finishGenerating = null;
let _getSelectedAgent = null;
let _getProjects = null;
let _getCurrentProjectSessionCount = null;
let _getSavedModeForAgent = null;
let _createOverlayPanel = null;
let _getPendingPasswordChangeValue = null;
let _setPendingPasswordChangeValue = null;
let _getModelConfigCache = null;
let _setModelConfigCache = null;
let _getCodexConfigCache = null;
let _setCodexConfigCache = null;
let _getIsGenerating = null;
let _setAuthToken = null;

export function setupSettingsHandlers(handlers) {
  _getLoginOverlay = handlers.getLoginOverlay;
  _getApp = handlers.getApp;
  _getLoginError = handlers.getLoginError;
  _getRememberPw = handlers.getRememberPw;
  _getLoginPassword = handlers.getLoginPassword;
  _restoreInitialSession = handlers.restoreInitialSession;
  _finishGenerating = handlers.finishGenerating;
  _getSelectedAgent = handlers.getSelectedAgent;
  _getProjects = handlers.getProjects;
  _getCurrentProjectSessionCount = handlers.getCurrentProjectSessionCount;
  _getSavedModeForAgent = handlers.getSavedModeForAgent;
  _createOverlayPanel = handlers.createOverlayPanel;
  _getPendingPasswordChangeValue = handlers.getPendingPasswordChangeValue;
  _setPendingPasswordChangeValue = handlers.setPendingPasswordChangeValue;
  _getModelConfigCache = handlers.getModelConfigCache;
  _setModelConfigCache = handlers.setModelConfigCache;
  _getCodexConfigCache = handlers.getCodexConfigCache;
  _setCodexConfigCache = handlers.setCodexConfigCache;
  _getIsGenerating = handlers.getIsGenerating;
  _setAuthToken = handlers.setAuthToken;

  const settingsBtn = $('#settings-btn');
  if (settingsBtn && !settingsBtn.dataset.settingsBound) {
    settingsBtn.addEventListener('click', showSettingsPanel);
    settingsBtn.dataset.settingsBound = '1';
  }
}

function getRememberPw() {
  return _getRememberPw?.() || null;
}

function getLoginPassword() {
  return _getLoginPassword?.() || null;
}

function getSelectedAgentValue() {
  return _getSelectedAgent?.() || 'claude';
}

function getProjectsValue() {
  return _getProjects?.() || [];
}

function getLoginOverlayEl() {
  return _getLoginOverlay?.() || null;
}

function getAppEl() {
  return _getApp?.() || null;
}

function getLoginErrorEl() {
  return _getLoginError?.() || null;
}

function getPendingPasswordChangeValue() {
  return _getPendingPasswordChangeValue ? _getPendingPasswordChangeValue() : null;
}

function setPendingPasswordChangeValue(value) {
  if (_setPendingPasswordChangeValue) _setPendingPasswordChangeValue(value);
}

function getModelConfigCacheValue() {
  return _getModelConfigCache ? _getModelConfigCache() : null;
}

function getCodexConfigCacheValue() {
  return _getCodexConfigCache ? _getCodexConfigCache() : null;
}

export function hasRememberedPassword() {
  return !!getRememberedPassword();
}

function getRememberedPassword() {
  return String(sessionStorage.getItem(REMEMBERED_PASSWORD_STORAGE_KEY) || '');
}

export function saveRememberedPassword(password) {
  const value = String(password || '');
  if (value) sessionStorage.setItem(REMEMBERED_PASSWORD_STORAGE_KEY, value);
  else sessionStorage.removeItem(REMEMBERED_PASSWORD_STORAGE_KEY);
}

export function clearRememberedPassword() {
  sessionStorage.removeItem(REMEMBERED_PASSWORD_STORAGE_KEY);
}

export function restoreRememberedPasswordInput() {
  const rememberedPassword = getRememberedPassword();
  const rememberPw = getRememberPw();
  const loginPassword = getLoginPassword();
  if (rememberPw) rememberPw.checked = !!rememberedPassword;
  if (!loginPassword) return;
  loginPassword.value = rememberedPassword;
}

export function getStoredTheme() {
  const raw = localStorage.getItem(THEME_STORAGE_KEY);
  return THEME_LABELS[raw] ? raw : DEFAULT_THEME;
}

export function applyTheme(theme, options = {}) {
  const nextTheme = THEME_LABELS[theme] ? theme : DEFAULT_THEME;
  document.documentElement.dataset.theme = nextTheme;
  if (document.body) {
    document.body.dataset.theme = nextTheme;
  }
  if (!options.skipPersist) {
    localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
  }
  return nextTheme;
}
let _onCodexSessions = null;
let _onNativeSessions = null;
const _onCwdSuggestions = null;
let _onDirectoryListing = null;
let _onPasswordChanged = null;

function renderFetchModelsResult(fetchStatus, datalist, result) {
  if (result.success) {
    datalist.innerHTML = (result.models || []).map((model) => `<option value="${escapeHtml(model)}">`).join('');
    fetchStatus.textContent = result.message || `获取到 ${result.models.length} 个模型`;
    fetchStatus.style.color = 'var(--text-success, #5dbe5d)';
    return;
  }
  fetchStatus.textContent = result.message || '获取失败';
  fetchStatus.style.color = 'var(--text-error, #e85d5d)';
}

const PROVIDER_OPTIONS = [
  { value: 'off', label: '关闭' },
  { value: 'pushplus', label: 'PushPlus' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'serverchan', label: 'Server酱' },
  { value: 'feishu', label: '飞书机器人' },
  { value: 'qqbot', label: 'QQ（Qmsg）' },
];

function buildUnifiedSettingsPanelHtml(providerOptions) {
  return `
    <h3>
      ⚙ 系统设置
      <button class="settings-close" title="关闭">&times;</button>
    </h3>

    <div class="settings-section-title">代理渠道</div>
    <div class="settings-field">
      <label>Claude 渠道</label>
      <select class="settings-select" id="unified-claude-mode"></select>
    </div>
    <div class="settings-field">
      <label>Codex 渠道</label>
      <div id="unified-codex-channel-area"></div>
    </div>

    <div class="settings-divider"></div>

    <div class="settings-section-title">AI 提供商</div>
    <div id="unified-template-area"></div>
    <div class="settings-actions">
      <button class="btn-save" id="unified-save-btn">保存渠道配置</button>
    </div>
    <div class="settings-status" id="unified-status"></div>

    <div class="settings-divider"></div>

    <div class="settings-section-title">通知设置</div>
    <div class="settings-field">
      <label>通知方式</label>
      <select class="settings-select" id="notify-provider">
        ${providerOptions.map((o) => `<option value="${o.value}">${escapeHtml(o.label)}</option>`).join('')}
      </select>
    </div>
    <div class="settings-field">
      <label>通知时机</label>
      <select class="settings-select" id="notify-completion-mode">
        <option value="background">仅后台任务完成时</option>
        <option value="always">任务完成时始终通知</option>
      </select>
    </div>
    <div class="settings-inline-note">测试按钮会立即发送一条测试消息；这里控制的是实际会话任务完成后的通知时机。</div>
    <div id="notify-fields"></div>
    <div class="settings-actions">
      <button class="btn-test" id="notify-test-btn">测试</button>
      <button class="btn-save" id="notify-save-btn">保存</button>
    </div>
    <div class="settings-status" id="notify-status"></div>

    <div class="settings-divider"></div>

    <div class="settings-section-title">界面主题</div>
    <div class="settings-field">
      <label>配色方案</label>
      <select class="settings-select" id="theme-select">
        <option value="default">默认主题</option>
        <option value="localhost">极简主题</option>
      </select>
    </div>
    <div class="settings-inline-note">切换工作台外观，不影响功能或会话数据。</div>
    <div class="settings-status" id="theme-status"></div>

    <div class="settings-divider"></div>

    <div class="settings-section-title">系统</div>
    <div class="settings-actions" style="margin-top:0;flex-wrap:wrap;gap:10px">
      <button class="btn-test" id="pw-open-modal-btn" style="padding:6px 16px">修改密码</button>
      <button class="btn-test" id="check-update-btn" style="padding:6px 16px">检查更新</button>
    </div>
    <div class="settings-status" id="update-status" style="margin-top:8px"></div>

    <div class="settings-divider"></div>

    <div class="settings-section-title">远程访问 (Cloudflare Tunnel)</div>
    <div id="tunnel-panel-content">
      <div class="settings-status" id="tunnel-install-status" style="margin-bottom:8px"></div>
      <div class="settings-actions" style="margin-top:0;gap:10px;align-items:center;flex-wrap:wrap">
        <button class="btn-save" id="tunnel-install-btn" style="padding:6px 16px;display:none">一键安装 cloudflared</button>
        <button class="btn-save" id="tunnel-start-btn" style="padding:6px 16px;display:none">开启 Tunnel</button>
        <button class="btn-test" id="tunnel-stop-btn" style="padding:6px 16px;display:none">关闭 Tunnel</button>
      </div>
      <div class="settings-status" id="tunnel-status" style="margin-top:8px"></div>
      <div id="tunnel-qr-area" style="margin-top:12px;display:none;text-align:center">
        <div id="tunnel-qr-canvas" style="display:inline-block;padding:10px;background:#fff;border-radius:8px"></div>
        <div style="font-size:11px;color:var(--text-secondary,#888);margin-top:6px">扫码访问</div>
      </div>
    </div>
  `;
}

function buildUnifiedTemplateModalHtml(current, draft) {
  return `
    <div class="settings-header">
      <h3>${current ? `编辑提供商: ${escapeHtml(current.name)}` : '新建 AI 提供商'}</h3>
      <button class="settings-close" id="unified-template-modal-close">&times;</button>
    </div>
    <div class="settings-field">
      <label>提供商名称</label>
      <input type="text" id="unified-template-name" value="${escapeHtml(draft.name || '')}">
    </div>
    <div class="settings-field">
      <label>API Key</label>
      <input type="text" id="unified-template-apikey" placeholder="sk-..." value="${escapeHtml(draft.apiKey || '')}">
    </div>
    <div class="settings-field">
      <label>API Base URL</label>
      <input type="text" id="unified-template-apibase" placeholder="https://api.openai.com/v1" value="${escapeHtml(draft.apiBase || '')}">
    </div>
    <div class="settings-field">
      <label>上游协议</label>
      <select class="settings-select" id="unified-template-upstream-type">
        <option value="openai" ${draft.upstreamType === 'anthropic' ? '' : 'selected'}>OpenAI / Responses</option>
        <option value="anthropic" ${draft.upstreamType === 'anthropic' ? 'selected' : ''}>Anthropic / Messages</option>
      </select>
    </div>

    <div class="settings-divider" style="margin:12px 0"></div>

    <div class="settings-field">
      <div class="tpl-fetch-toolbar">
        <div class="tpl-fetch-toolbar-title">获取上游模型列表</div>
        <button class="btn-test tpl-fetch-toolbar-btn" id="unified-template-fetch-models">获取模型</button>
        <span id="unified-template-fetch-status" class="tpl-fetch-toolbar-status"></span>
      </div>
    </div>

    <div class="settings-divider" style="margin:12px 0"></div>

    <div class="settings-field">
      <label>默认模型</label>
      <input type="text" id="unified-template-default" list="unified-template-models" placeholder="例如 claude-sonnet-4-6 / gpt-5.4 / gemini-2.5-pro" value="${escapeHtml(draft.defaultModel || '')}" autocomplete="off">
    </div>
    <div class="settings-field">
      <label>Opus 模型名</label>
      <input type="text" id="unified-template-opus" list="unified-template-models" placeholder="claude-opus-4-6" value="${escapeHtml(draft.opusModel || '')}" autocomplete="off">
    </div>
    <div class="settings-field">
      <label>Sonnet 模型名</label>
      <input type="text" id="unified-template-sonnet" list="unified-template-models" placeholder="claude-sonnet-4-6" value="${escapeHtml(draft.sonnetModel || '')}" autocomplete="off">
    </div>
    <div class="settings-field">
      <label>Haiku 模型名</label>
      <input type="text" id="unified-template-haiku" list="unified-template-models" placeholder="claude-haiku-4-5" value="${escapeHtml(draft.haikuModel || '')}" autocomplete="off">
    </div>
    <datalist id="unified-template-models"></datalist>
    <div class="settings-inline-note">
      这套 AI 提供商配置会出现在 Claude 和 Codex 的渠道下拉框里。Codex 主要使用 API Key、API Base URL 和“默认模型”；Claude 还会读取 Opus / Sonnet / Haiku 这三个模型映射。
    </div>
    <div class="settings-actions">
      <button class="btn-save" id="unified-template-ok">确定</button>
    </div>
  `;
}

function buildPasswordModalHtml() {
  return `
    <div class="settings-header">
      <h3>修改密码</h3>
      <button class="settings-close" id="pw-modal-close">&times;</button>
    </div>
    <div class="settings-field">
      <label>当前密码</label>
      <input type="password" id="pw-modal-current" placeholder="输入当前密码" autocomplete="current-password">
    </div>
    <div class="settings-field">
      <label>新密码</label>
      <input type="password" id="pw-modal-new" placeholder="新密码" autocomplete="new-password">
      <div class="password-hint" id="pw-modal-hint">至少 8 位，包含大写/小写/数字/特殊字符中的 2 种</div>
    </div>
    <div class="settings-field">
      <label>确认新密码</label>
      <input type="password" id="pw-modal-confirm" placeholder="确认新密码" autocomplete="new-password">
    </div>
    <div class="settings-actions">
      <button class="btn-save" id="pw-modal-submit" disabled>修改密码</button>
    </div>
    <div class="settings-status" id="pw-modal-status"></div>
  `;
}

function showUnifiedSettingsPanel() {
  clearSettingsPanelSubscriptions();
  const { overlay, panel } = _createOverlayPanel({
    overlayId: 'settings-overlay',
    panelHtml: buildUnifiedSettingsPanelHtml(PROVIDER_OPTIONS),
  });

  const closeBtn = panel.querySelector('.settings-close');
  const claudeModeSelect = panel.querySelector('#unified-claude-mode');
  const codexChannelArea = panel.querySelector('#unified-codex-channel-area');
  const templateArea = panel.querySelector('#unified-template-area');
  const unifiedSaveBtn = panel.querySelector('#unified-save-btn');
  const unifiedStatusDiv = panel.querySelector('#unified-status');

  const providerSelect = panel.querySelector('#notify-provider');
  const completionModeSelect = panel.querySelector('#notify-completion-mode');
  const fieldsDiv = panel.querySelector('#notify-fields');
  const notifyStatusDiv = panel.querySelector('#notify-status');
  const testBtn = panel.querySelector('#notify-test-btn');
  const saveBtn = panel.querySelector('#notify-save-btn');

  const themeSelect = panel.querySelector('#theme-select');
  const themeStatusDiv = panel.querySelector('#theme-status');

  const pwOpenModalBtn = panel.querySelector('#pw-open-modal-btn');
  const checkUpdateBtn = panel.querySelector('#check-update-btn');
  const updateStatusEl = panel.querySelector('#update-status');

  const tunnelInstallBtn = panel.querySelector('#tunnel-install-btn');
  const tunnelStartBtn = panel.querySelector('#tunnel-start-btn');
  const tunnelStopBtn = panel.querySelector('#tunnel-stop-btn');
  const tunnelStatusEl = panel.querySelector('#tunnel-status');
  const tunnelInstallStatusEl = panel.querySelector('#tunnel-install-status');
  const tunnelQrArea = panel.querySelector('#tunnel-qr-area');
  const tunnelQrCanvas = panel.querySelector('#tunnel-qr-canvas');

  function drawQrCode(url) {
    tunnelQrCanvas.innerHTML = '';
    try {
      if (typeof QRCode === 'undefined') throw new Error('QRCode not loaded');
      new QRCode(tunnelQrCanvas, {
        text: url,
        width: 160,
        height: 160,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.M,
      });
      tunnelQrArea.style.display = 'block';
    } catch (e) {
      console.error('[tunnel] QR generation failed:', e);
      tunnelQrArea.style.display = 'none';
    }
  }

  function applyTunnelStatus({ running, url, installed }) {
    tunnelInstallBtn.style.display = (!installed) ? '' : 'none';
    tunnelStartBtn.style.display = (installed && !running) ? '' : 'none';
    tunnelStopBtn.style.display = (installed && running) ? '' : 'none';

    if (!installed) {
      tunnelInstallStatusEl.textContent = 'cloudflared 未安装，点击下方按钮自动安装（约 30-50 MB）';
      tunnelInstallStatusEl.className = 'settings-status';
      tunnelStatusEl.textContent = '';
      tunnelQrArea.style.display = 'none';
    } else if (running) {
      tunnelInstallStatusEl.textContent = '';
      if (url) {
        tunnelStatusEl.innerHTML = '<div style="margin-top:4px"><div style="font-size:11px;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:4px">公网地址</div><div style="display:flex;align-items:stretch;border:2px solid var(--line)"><a href="' + escapeHtml(url) + '" target="_blank" rel="noopener" style="flex:1;min-width:0;font-size:11px;word-break:break-all;padding:6px 8px;background:var(--bg-secondary);color:var(--text-primary);font-family:var(--font-mono)">' + escapeHtml(url) + '</a><button id="tunnel-copy-btn">复制</button></div></div>';
        tunnelStatusEl.className = 'settings-status';
        const copyBtn = tunnelStatusEl.querySelector('#tunnel-copy-btn');
        if (copyBtn) copyBtn.addEventListener('click', () => { navigator.clipboard?.writeText(url); copyBtn.textContent = '已复制'; setTimeout(() => { copyBtn.textContent = '复制'; }, 2000); });
        drawQrCode(url);
      } else {
        tunnelStatusEl.textContent = '正在获取公网 URL...';
        tunnelStatusEl.className = 'settings-status';
        tunnelQrArea.style.display = 'none';
      }
    } else {
      tunnelInstallStatusEl.textContent = '';
      tunnelStatusEl.textContent = '';
      tunnelQrArea.style.display = 'none';
    }
  }

  tunnelInstallBtn.addEventListener('click', () => {
    tunnelInstallBtn.disabled = true;
    tunnelInstallStatusEl.textContent = '正在下载安装，请稍候...';
    tunnelInstallStatusEl.className = 'settings-status';
    send({ type: 'install_cloudflared' });
  });
  tunnelStartBtn.addEventListener('click', () => {
    tunnelStartBtn.disabled = true;
    tunnelStatusEl.textContent = '正在启动，请稍候（首次可能需要 30 秒）...';
    tunnelStatusEl.className = 'settings-status';
    send({ type: 'tunnel_start' });
  });
  tunnelStopBtn.addEventListener('click', () => {
    tunnelStopBtn.disabled = true;
    send({ type: 'tunnel_stop' });
  });

  let currentNotifyConfig = null;
  let currentCodexConfig = null;
  let modelConfigLoaded = false;
  let codexConfigLoaded = false;
  let modelEditingTemplates = [];
  let providerEditorSelection = '';
  let selectedClaudeChannel = 'local';
  let codexProviderState = buildCodexProviderSettingsState(null);
  let _onUpdateInfo = null;

  function syncUnifiedControlsState() {
    const ready = modelConfigLoaded && codexConfigLoaded;
    claudeModeSelect.disabled = !ready;
    codexChannelArea.querySelectorAll('button,select').forEach((el) => { el.disabled = !ready; });
    unifiedSaveBtn.disabled = !ready;
  }

  function applyModelConfigToPanel(config, markLoaded = true) {
    const normalized = config || { mode: 'local', activeTemplate: '', templates: [] };
    if (markLoaded) modelConfigLoaded = true;
    modelEditingTemplates = (normalized.templates || []).map((template) => ({
      ...template,
      originalName: template.originalName || template.name || '',
    }));
    providerEditorSelection = normalized.activeTemplate || providerEditorSelection || (modelEditingTemplates[0]?.name || '');
    selectedClaudeChannel = normalized.mode === 'custom'
      ? (normalized.activeTemplate || modelEditingTemplates[0]?.name || 'local')
      : 'local';
  }

  function applyCodexConfigToPanel(config, markLoaded = true) {
    if (markLoaded) codexConfigLoaded = true;
    currentCodexConfig = config || { mode: 'local', legacyMode: '' };
    codexProviderState = buildCodexProviderSettingsState(currentCodexConfig, modelEditingTemplates[0]?.name || '');
  }

  function showUnifiedStatus(msg, type) {
    unifiedStatusDiv.textContent = msg;
    unifiedStatusDiv.className = 'settings-status ' + (type || '');
  }

  function showNotifyStatus(msg, type) {
    notifyStatusDiv.textContent = msg;
    notifyStatusDiv.className = 'settings-status ' + (type || '');
  }

  function showThemeStatus(msg, type) {
    themeStatusDiv.textContent = msg;
    themeStatusDiv.className = 'settings-status ' + (type || '');
  }

  function renderFields(provider) {
    renderNotifyFields(fieldsDiv, currentNotifyConfig, provider);
  }

  function collectNotifyConfig() {
    return collectNotifyConfigFromPanel(panel, currentNotifyConfig, providerSelect.value);
  }

  function ensureChannelSelections() {
    if (!modelEditingTemplates.length) {
      providerEditorSelection = '';
      selectedClaudeChannel = 'local';
      codexProviderState.templateProvider = '';
      return;
    }

    const names = new Set(modelEditingTemplates.map((template) => template.name));
    if (!names.has(providerEditorSelection)) {
      providerEditorSelection = modelEditingTemplates[0].name;
    }
    if (selectedClaudeChannel !== 'local' && !names.has(selectedClaudeChannel)) {
      selectedClaudeChannel = modelEditingTemplates[0].name;
    }
    if (codexProviderState.templateProvider && !names.has(codexProviderState.templateProvider)) {
      codexProviderState.templateProvider = modelEditingTemplates[0].name;
    }
  }

  function renderAgentChannelOptions() {
    ensureChannelSelections();
    const providerOptions = modelEditingTemplates.map((template) =>
      `<option value="${escapeHtml(template.name)}">${escapeHtml(template.name)}</option>`
    ).join('');
    claudeModeSelect.innerHTML = `
      <option value="local">读取本地 Claude 配置</option>
      ${providerOptions}
    `;
    claudeModeSelect.value = selectedClaudeChannel;
    renderCodexChannelArea();
  }

  function renderCodexChannelArea() {
    renderCodexProviderSettings({
      container: codexChannelArea,
      state: codexProviderState,
      codexConfig: currentCodexConfig,
      templates: modelEditingTemplates,
    });
  }

  function renderTemplateArea() {
    if (!modelConfigLoaded || !codexConfigLoaded) {
      templateArea.innerHTML = `
        <div class="settings-inline-note">
          正在加载 AI 提供商配置...
        </div>
      `;
      return;
    }

    renderAgentChannelOptions();
    const legacyMode = currentCodexConfig?.legacyMode || '';
    const legacyNote = legacyMode === 'custom'
      ? '检测到旧版 Codex 独立配置。当前页面不再维护那套分叉逻辑；你保存后，Codex 会改为从上面的 AI 提供商列表里单独选渠道。'
      : (legacyMode === 'shared'
        ? '检测到旧版“复用 Claude 配置”模式。当前页面已经改成独立渠道逻辑，保存后 Claude 和 Codex 可以分别选择不同的 AI 提供商。'
        : '');

    if (!modelEditingTemplates.length) {
      templateArea.innerHTML = `
        <div class="settings-inline-note">
          AI 提供商列表为空。只要 Claude 或 Codex 有一个需要走远程接口，就先在这里新建至少一套提供商配置。
        </div>
        ${legacyNote ? `<div class="settings-inline-note warning">${legacyNote}</div>` : ''}
        <div class="settings-actions" style="margin-top:0">
          <button class="btn-test" id="unified-template-add-first">+ 新建提供商</button>
        </div>
      `;
      panel.querySelector('#unified-template-add-first').addEventListener('click', () => openUnifiedTemplateModal());
      return;
    }

    const options = modelEditingTemplates.map((template) =>
      `<option value="${escapeHtml(template.name)}" ${template.name === providerEditorSelection ? 'selected' : ''}>${escapeHtml(template.name)}</option>`
    ).join('');
    templateArea.innerHTML = `
      ${legacyNote ? `<div class="settings-inline-note warning">${legacyNote}</div>` : ''}
      <div class="settings-field">
        <label>提供商列表</label>
        <div style="display:flex;gap:6px;align-items:center">
          <select class="settings-select" id="unified-template-select" style="flex:1">
            ${options}
            <option value="__new__">+ 新建提供商</option>
          </select>
          <button class="btn-test" id="unified-template-edit" style="padding:4px 10px">编辑</button>
          <button class="btn-test" id="unified-template-del" title="删除" style="padding:4px 8px">删除</button>
        </div>
      </div>
    `;

    panel.querySelector('#unified-template-select').addEventListener('change', (e) => {
      if (e.target.value === '__new__') {
        openUnifiedTemplateModal();
        return;
      }
      providerEditorSelection = e.target.value;
      renderTemplateArea();
    });

    panel.querySelector('#unified-template-edit').addEventListener('click', () => {
      openUnifiedTemplateModal(providerEditorSelection);
    });

    panel.querySelector('#unified-template-del').addEventListener('click', () => {
      if (!providerEditorSelection) return;
      if (!confirm(`确认删除提供商「${providerEditorSelection}」?`)) return;
      modelEditingTemplates = modelEditingTemplates.filter((template) => template.name !== providerEditorSelection);
      providerEditorSelection = modelEditingTemplates[0]?.name || '';
      renderTemplateArea();
    });
  }

  function openUnifiedTemplateModal(templateName = '') {
    const current = templateName
      ? modelEditingTemplates.find((template) => template.name === templateName)
      : null;
    const draft = current || {
      name: '',
      originalName: '',
      apiKey: '',
      apiBase: '',
      upstreamType: 'openai',
      defaultModel: '',
      opusModel: '',
      sonnetModel: '',
      haikuModel: '',
    };

    const { overlay: modalOverlay, panel: modal, close: closeModal } = _createOverlayPanel({
      zIndex: '10001',
      maxWidth: '460px',
      panelHtml: buildUnifiedTemplateModalHtml(current, draft),
    });

    const fetchBtn = modal.querySelector('#unified-template-fetch-models');
    const fetchStatus = modal.querySelector('#unified-template-fetch-status');
    const datalist = modal.querySelector('#unified-template-models');
    let disposeFetchModelsResult = null;

    fetchBtn.addEventListener('click', () => {
      const apiBase = modal.querySelector('#unified-template-apibase').value.trim();
      const apiKey = modal.querySelector('#unified-template-apikey').value.trim();
      const upstreamType = modal.querySelector('#unified-template-upstream-type').value;
      if (!apiBase || !apiKey) {
        fetchStatus.textContent = '请先填写 API Base 和 API Key';
        fetchStatus.style.color = 'var(--text-error, #e85d5d)';
        return;
      }
      fetchBtn.disabled = true;
      fetchStatus.textContent = '正在获取...';
      fetchStatus.style.color = 'var(--text-secondary)';

      if (disposeFetchModelsResult) disposeFetchModelsResult();
      disposeFetchModelsResult = onWsEvent('fetch_models_result', (result) => {
        disposeFetchModelsResult = null;
        fetchBtn.disabled = false;
        renderFetchModelsResult(fetchStatus, datalist, result);
      }, { once: true });

      send({
        type: 'fetch_models',
        apiBase,
        apiKey,
        upstreamType,
        templateName: current?.name || modal.querySelector('#unified-template-name').value.trim(),
      });
    });

    const closeTemplateModal = () => {
      if (disposeFetchModelsResult) {
        disposeFetchModelsResult();
        disposeFetchModelsResult = null;
      }
      closeModal();
    };

    modal.querySelector('#unified-template-modal-close').addEventListener('click', closeTemplateModal);
    modalOverlay.addEventListener('click', (e) => { if (e.target === modalOverlay) closeTemplateModal(); });

    modal.querySelector('#unified-template-ok').addEventListener('click', () => {
      const name = modal.querySelector('#unified-template-name').value.trim();
      const apiKey = modal.querySelector('#unified-template-apikey').value.trim();
      const apiBase = modal.querySelector('#unified-template-apibase').value.trim();
      const upstreamType = modal.querySelector('#unified-template-upstream-type').value;
      const defaultModel = modal.querySelector('#unified-template-default').value.trim();
      const opusModel = modal.querySelector('#unified-template-opus').value.trim();
      const sonnetModel = modal.querySelector('#unified-template-sonnet').value.trim();
      const haikuModel = modal.querySelector('#unified-template-haiku').value.trim();

      if (!name) {
        alert('请填写提供商名称');
        return;
      }
      if (!apiKey) {
        alert('请填写 API Key');
        return;
      }
      if (!apiBase) {
        alert('请填写 API Base URL');
        return;
      }

      const existing = modelEditingTemplates.find((template) => template.name === name);
      if (existing && existing !== current) {
        alert('提供商名称已存在');
        return;
      }

      if (current) {
        const previousName = current.name;
        if (!current.originalName) current.originalName = current.name;
        current.name = name;
        current.apiKey = apiKey;
        current.apiBase = apiBase;
        current.upstreamType = upstreamType;
        current.defaultModel = defaultModel;
        current.opusModel = opusModel;
        current.sonnetModel = sonnetModel;
        current.haikuModel = haikuModel;
        if (providerEditorSelection === previousName) providerEditorSelection = name;
        if (selectedClaudeChannel === previousName) selectedClaudeChannel = name;
        if (codexProviderState.templateProvider === previousName) codexProviderState.templateProvider = name;
      } else {
        modelEditingTemplates.push({
          name,
          originalName: '',
          apiKey,
          apiBase,
          upstreamType,
          defaultModel,
          opusModel,
          sonnetModel,
          haikuModel,
        });
        if (!providerEditorSelection) providerEditorSelection = name;
      }

      providerEditorSelection = name;
      closeTemplateModal();
      renderTemplateArea();
    });
  }

  claudeModeSelect.addEventListener('change', () => {
    selectedClaudeChannel = claudeModeSelect.value;
    renderTemplateArea();
  });
  themeSelect.value = getStoredTheme();
  themeSelect.addEventListener('change', () => {
    const nextTheme = applyTheme(themeSelect.value);
    themeSelect.value = nextTheme;
    showThemeStatus(`已切换为 ${THEME_LABELS[nextTheme]}`, 'success');
  });
  providerSelect.addEventListener('change', () => renderFields(providerSelect.value));

  unifiedSaveBtn.addEventListener('click', async () => {
    if (!modelConfigLoaded || !codexConfigLoaded) {
      showUnifiedStatus('正在同步当前配置，请稍后再保存', 'error');
      return;
    }

    ensureChannelSelections();
    const claudeUsesProvider = selectedClaudeChannel !== 'local';
    const codexSaveConfig = buildCodexSaveConfig(codexProviderState);
    const codexUsesTemplate = codexSaveConfig.mode === 'template';
    const needsProvider = claudeUsesProvider || codexUsesTemplate;
    if (needsProvider && modelEditingTemplates.length === 0) {
      showUnifiedStatus('至少需要一个 AI 提供商配置', 'error');
      return;
    }

    const claudeTemplate = claudeUsesProvider
      ? modelEditingTemplates.find((template) => template.name === selectedClaudeChannel) || null
      : null;
    const codexTemplate = codexUsesTemplate
      ? modelEditingTemplates.find((template) => template.name === codexSaveConfig.sharedTemplate) || null
      : null;

    if (claudeUsesProvider && (!claudeTemplate || !claudeTemplate.apiKey || !claudeTemplate.apiBase)) {
      showUnifiedStatus('Claude 选中的 AI 提供商缺少 API Key 或 API Base URL', 'error');
      return;
    }
    if (codexUsesTemplate && (!codexTemplate || !codexTemplate.apiKey || !codexTemplate.apiBase)) {
      showUnifiedStatus('Codex 选中的 AI 提供商缺少 API Key 或 API Base URL', 'error');
      return;
    }

    showUnifiedStatus('正在保存...', '');
    const waitModelConfig = waitForWsEvent('model_config', { timeoutMs: 8000 });
    const waitCodexConfig = waitForWsEvent('codex_config', { timeoutMs: 8000 });
    send({
      type: 'save_model_config',
      config: {
        mode: claudeUsesProvider ? 'custom' : 'local',
        activeTemplate: claudeUsesProvider ? selectedClaudeChannel : '',
        templates: modelEditingTemplates,
      },
    });
    send({
      type: 'save_codex_config',
      config: codexSaveConfig,
    });
    try {
      await Promise.all([waitModelConfig, waitCodexConfig]);
      showUnifiedStatus('已保存', 'success');
    } catch (error) {
      showUnifiedStatus(error?.message || '保存失败，请重试', 'error');
    }
  });

  testBtn.addEventListener('click', () => {
    const config = collectNotifyConfig();
    send({ type: 'save_notify_config', config });
    showNotifyStatus('正在发送测试消息...', '');
    send({ type: 'test_notify' });
  });

  saveBtn.addEventListener('click', async () => {
    const config = collectNotifyConfig();
    showNotifyStatus('正在保存...', '');
    const waitNotifyConfig = waitForWsEvent('notify_config', { timeoutMs: 8000 });
    send({ type: 'save_notify_config', config });
    try {
      await waitNotifyConfig;
      showNotifyStatus('已保存', 'success');
    } catch (error) {
      showNotifyStatus(error?.message || '保存失败，请重试', 'error');
    }
  });

  pwOpenModalBtn.addEventListener('click', openPasswordModal);

  checkUpdateBtn.addEventListener('click', () => {
    updateStatusEl.textContent = '正在检查...';
    updateStatusEl.className = 'settings-status';
    _onUpdateInfo = (info) => {
      _onUpdateInfo = null;
      if (info.error) {
        updateStatusEl.textContent = '检查失败: ' + info.error;
        updateStatusEl.className = 'settings-status error';
        return;
      }
      if (info.hasUpdate) {
        updateStatusEl.innerHTML = buildUpdateStatusHtml(info);
        updateStatusEl.className = 'settings-status success';
      } else {
        updateStatusEl.textContent = `已是最新版本 v${info.localVersion}`;
        updateStatusEl.className = 'settings-status success';
      }
    };
    send({ type: 'check_update' });
  });

  registerSettingsPanelHandler('notify_config', (config) => {
    currentNotifyConfig = config;
    providerSelect.value = config.provider || 'off';
    completionModeSelect.value = config.completionMode === 'always' ? 'always' : 'background';
    renderFields(config.provider || 'off');
  });

  registerSettingsPanelHandler('notify_test_result', (msg) => {
    showNotifyStatus(msg.message, msg.success ? 'success' : 'error');
  });

  registerSettingsPanelHandler('model_config', (config) => {
    applyModelConfigToPanel(config);
    syncUnifiedControlsState();
    renderTemplateArea();
  });

  registerSettingsPanelHandler('codex_config', (config) => {
    applyCodexConfigToPanel(config);
    syncUnifiedControlsState();
    renderTemplateArea();
  });

  registerSettingsPanelHandler('tunnel_status', (msg) => {
    applyTunnelStatus(msg);
  });

  registerSettingsPanelHandler('tunnel_install_progress', (msg) => {
    if (msg.error) {
      tunnelInstallStatusEl.textContent = '安装失败：' + msg.error;
      tunnelInstallStatusEl.className = 'settings-status error';
      tunnelInstallBtn.disabled = false;
    } else if (msg.done) {
      tunnelInstallStatusEl.textContent = msg.message || '安装完成！';
      tunnelInstallStatusEl.className = 'settings-status success';
      tunnelInstallBtn.disabled = false;
    } else {
      tunnelInstallStatusEl.textContent = msg.message || '安装中...';
      tunnelInstallStatusEl.className = 'settings-status';
    }
  });

  closeBtn.addEventListener('click', hideSettingsPanel);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) hideSettingsPanel(); });
  window._ccOnUpdateInfo = (info) => { if (_onUpdateInfo) _onUpdateInfo(info); };
  document.addEventListener('keydown', _settingsEscape);

  const modelConfigCache = getModelConfigCacheValue();
  const codexConfigCache = getCodexConfigCacheValue();
  if (modelConfigCache) applyModelConfigToPanel(modelConfigCache, false);
  if (codexConfigCache) applyCodexConfigToPanel(codexConfigCache, false);
  syncUnifiedControlsState();
  renderTemplateArea();
  send({ type: 'get_notify_config' });
  send({ type: 'get_model_config' });
  send({ type: 'get_codex_config' });
  send({ type: 'get_tunnel_status' });
}

function buildNotifyFieldsHtml(config, provider) {
  if (provider === 'pushplus') {
    return `
      <div class="settings-field">
        <label>Token</label>
        <input type="text" id="notify-pushplus-token" placeholder="PushPlus Token" value="${escapeHtml(config?.pushplus?.token || '')}">
      </div>
    `;
  }
  if (provider === 'telegram') {
    return `
      <div class="settings-field">
        <label>Bot Token</label>
        <input type="text" id="notify-tg-bottoken" placeholder="123456:ABC-DEF..." value="${escapeHtml(config?.telegram?.botToken || '')}">
      </div>
      <div class="settings-field">
        <label>Chat ID</label>
        <input type="text" id="notify-tg-chatid" placeholder="Chat ID" value="${escapeHtml(config?.telegram?.chatId || '')}">
      </div>
    `;
  }
  if (provider === 'serverchan') {
    return `
      <div class="settings-field">
        <label>SendKey</label>
        <input type="text" id="notify-sc-sendkey" placeholder="Server酱 SendKey" value="${escapeHtml(config?.serverchan?.sendKey || '')}">
      </div>
    `;
  }
  if (provider === 'feishu') {
    return `
      <div class="settings-field">
        <label>Webhook 地址</label>
        <input type="text" id="notify-feishu-webhook" placeholder="https://open.feishu.cn/open-apis/bot/v2/hook/xxx" value="${escapeHtml(config?.feishu?.webhook || '')}">
      </div>
    `;
  }
  if (provider === 'qqbot') {
    return `
      <div class="settings-field">
        <label>Qmsg Key</label>
        <input type="text" id="notify-qmsg-key" placeholder="Qmsg 推送 Key" value="${escapeHtml(config?.qqbot?.qmsgKey || '')}">
      </div>
    `;
  }
  return '';
}

function buildAgentContextCard(agent, title, copy) {
  const label = AGENT_LABELS[normalizeAgent(agent)] || AGENT_LABELS.claude;
  return `
    <div class="agent-context-card">
      <div class="agent-context-kicker">${escapeHtml(label)} Space</div>
      <div class="agent-context-title">${escapeHtml(title)}</div>
      <div class="agent-context-copy">${escapeHtml(copy)}</div>
    </div>
  `;
}

function buildImportModalBody(agent, title, copy, extraHtml = '') {
  return `${buildAgentContextCard(agent, title, copy)}${extraHtml}`;
}

function createImportSessionsModal({
  overlayId,
  title,
  closeBtnId,
  bodyId,
  agent,
  cardTitle,
  cardCopy,
  loadingText,
  onClose,
}) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = overlayId;
  overlay.innerHTML = `
    <div class="modal-panel modal-panel-wide">
      <div class="modal-header">
        <span class="modal-title">${escapeHtml(title)}</span>
        <button class="modal-close-btn" id="${escapeHtml(closeBtnId)}">✕</button>
      </div>
      <div class="modal-body" id="${escapeHtml(bodyId)}">
        ${buildImportModalBody(agent, cardTitle, cardCopy, `<div class="modal-loading">${escapeHtml(loadingText)}</div>`)}
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  const body = overlay.querySelector(`#${bodyId}`);

  function renderBody(extraHtml = '') {
    if (!body) return null;
    body.innerHTML = buildImportModalBody(agent, cardTitle, cardCopy, extraHtml);
    return body;
  }

  function close() {
    overlay.remove();
    if (typeof onClose === 'function') onClose();
  }

  overlay.querySelector(`#${closeBtnId}`)?.addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  return {
    overlay,
    body,
    close,
    renderBody,
    renderEmpty: (message) => renderBody(`<div class="modal-empty">${escapeHtml(message)}</div>`),
  };
}

function renderNotifyFields(fieldsDiv, config, provider) {
  fieldsDiv.innerHTML = buildNotifyFieldsHtml(config, provider);
}

function collectNotifyConfigFromPanel(panel, currentConfig, provider) {
  const completionModeSelect = panel.querySelector('#notify-completion-mode');
  const pp = panel.querySelector('#notify-pushplus-token');
  const tgBot = panel.querySelector('#notify-tg-bottoken');
  const tgChat = panel.querySelector('#notify-tg-chatid');
  const sc = panel.querySelector('#notify-sc-sendkey');
  const feishuWh = panel.querySelector('#notify-feishu-webhook');
  const qmsgKey = panel.querySelector('#notify-qmsg-key');
  return {
    provider,
    completionMode: completionModeSelect?.value === 'always' ? 'always' : (currentConfig?.completionMode || 'background'),
    pushplus: { token: pp ? pp.value.trim() : (currentConfig?.pushplus?.token || '') },
    telegram: {
      botToken: tgBot ? tgBot.value.trim() : (currentConfig?.telegram?.botToken || ''),
      chatId: tgChat ? tgChat.value.trim() : (currentConfig?.telegram?.chatId || ''),
    },
    serverchan: { sendKey: sc ? sc.value.trim() : (currentConfig?.serverchan?.sendKey || '') },
    feishu: { webhook: feishuWh ? feishuWh.value.trim() : (currentConfig?.feishu?.webhook || '') },
    qqbot: { qmsgKey: qmsgKey ? qmsgKey.value.trim() : (currentConfig?.qqbot?.qmsgKey || '') },
  };
}

function openPasswordModal() {
  const { overlay: pwOverlay, panel: pwModal, close: closePwModal } = _createOverlayPanel({
    zIndex: '10001',
    maxWidth: '400px',
    panelHtml: buildPasswordModalHtml(),
  });

  const newPwIn = pwModal.querySelector('#pw-modal-new');
  const confirmPwIn = pwModal.querySelector('#pw-modal-confirm');
  const currentPwIn = pwModal.querySelector('#pw-modal-current');
  const hint = pwModal.querySelector('#pw-modal-hint');
  const submitBtn = pwModal.querySelector('#pw-modal-submit');
  const status = pwModal.querySelector('#pw-modal-status');

  function checkPw() {
    const currentPw = currentPwIn.value;
    const newPw = newPwIn.value;
    const confirmPw = confirmPwIn.value;
    
    // Check if current password is entered
    if (!currentPw) {
      submitBtn.disabled = true;
      return;
    }
    
    if (!newPw) {
      hint.textContent = '至少 8 位，包含大写/小写/数字/特殊字符中的 2 种';
      hint.className = 'password-hint';
      submitBtn.disabled = true;
      return;
    }
    const result = clientValidatePassword(newPw);
    if (!result.valid) {
      hint.textContent = result.message;
      hint.className = 'password-hint error';
      submitBtn.disabled = true;
      return;
    }
    hint.textContent = '密码强度符合要求';
    hint.className = 'password-hint success';
    submitBtn.disabled = !confirmPw || confirmPw !== newPw;
  }

  currentPwIn.addEventListener('input', checkPw);
  newPwIn.addEventListener('input', checkPw);
  confirmPwIn.addEventListener('input', checkPw);

  // Password change timeout cleanup
  let pwChangeTimeoutId = null;
  const closePwModalWithCleanup = () => {
    clearTimeout(pwChangeTimeoutId);
    pwChangeTimeoutId = null;
    _onPasswordChanged = null;
    closePwModal();
  };

  pwModal.querySelector('#pw-modal-close').addEventListener('click', closePwModalWithCleanup);
  pwOverlay.addEventListener('click', (e) => { if (e.target === pwOverlay) closePwModalWithCleanup(); });

  submitBtn.addEventListener('click', () => {
    const currentPw = currentPwIn.value;
    const newPw = newPwIn.value;
    const confirmPw = confirmPwIn.value;
    
    if (!currentPw) {
      status.textContent = '请输入当前密码';
      status.className = 'settings-status error';
      return;
    }
    if (newPw !== confirmPw) {
      status.textContent = '两次密码不一致';
      status.className = 'settings-status error';
      return;
    }
    submitBtn.disabled = true;
    status.textContent = '正在修改...';
    status.className = 'settings-status';
    _onPasswordChanged = (result) => {
      clearTimeout(pwChangeTimeoutId);
      pwChangeTimeoutId = null;
      if (result.success) {
        status.textContent = result.message || '密码修改成功';
        status.className = 'settings-status success';
        // Clear password fields on success
        currentPwIn.value = '';
        newPwIn.value = '';
        confirmPwIn.value = '';
        setTimeout(closePwModalWithCleanup, 1200);
      } else {
        status.textContent = result.message || '修改失败';
        status.className = 'settings-status error';
        submitBtn.disabled = false;
      }
    };
    // Timeout cleanup for password change callback
    pwChangeTimeoutId = setTimeout(() => {
      _onPasswordChanged = null;
      pwChangeTimeoutId = null;
      status.textContent = '请求超时，请重试';
      status.className = 'settings-status error';
      submitBtn.disabled = false;
    }, 15000);
    setPendingPasswordChangeValue(newPw);
    send({ type: 'change_password', currentPassword: currentPw, newPassword: newPw });
  });

  newPwIn.focus();
}

export function showSettingsPanel() {
  showUnifiedSettingsPanel();
}

export function hideSettingsPanel() {
  const overlay = document.getElementById('settings-overlay');
  if (overlay) overlay.remove();
  document.querySelectorAll('.settings-subpage-overlay').forEach((node) => node.remove());
  clearSettingsPanelSubscriptions();
  window._ccOnUpdateInfo = null;
  document.removeEventListener('keydown', _settingsEscape);
}

function _settingsEscape(e) {
  if (e.key === 'Escape') hideSettingsPanel();
}

// --- Force Change Password ---
export function showForceChangePassword() {
  const overlay = document.createElement('div');
  overlay.className = 'force-change-overlay';
  overlay.id = 'force-change-overlay';

  const panel = document.createElement('div');
  panel.className = 'force-change-panel';

  panel.innerHTML = `
    <div class="login-logo">CC</div>
    <h2>修改初始密码</h2>
    <p>首次登录需要设置新密码</p>
    <div class="force-change-form">
      <input type="password" id="fc-new-pw" placeholder="新密码" autocomplete="new-password">
      <div class="password-hint" id="fc-hint">至少 8 位，包含大写/小写/数字/特殊字符中的 2 种</div>
      <input type="password" id="fc-confirm-pw" placeholder="确认新密码" autocomplete="new-password">
      <button id="fc-submit-btn" class="fc-submit-btn" disabled>确认修改</button>
      <div class="fc-status" id="fc-status"></div>
    </div>
  `;

  overlay.appendChild(panel);
  document.body.appendChild(overlay);

  const newPwInput = panel.querySelector('#fc-new-pw');
  const confirmPwInput = panel.querySelector('#fc-confirm-pw');
  const hintEl = panel.querySelector('#fc-hint');
  const submitBtn = panel.querySelector('#fc-submit-btn');
  const statusEl = panel.querySelector('#fc-status');

  function checkStrength() {
    const pw = newPwInput.value;
    const confirmPw = confirmPwInput.value;
    if (!pw) {
      hintEl.textContent = '至少 8 位，包含大写/小写/数字/特殊字符中的 2 种';
      hintEl.className = 'password-hint';
      submitBtn.disabled = true;
      return;
    }
    const result = clientValidatePassword(pw);
    if (!result.valid) {
      hintEl.textContent = result.message;
      hintEl.className = 'password-hint error';
      submitBtn.disabled = true;
      return;
    }
    hintEl.textContent = '密码强度符合要求';
    hintEl.className = 'password-hint success';
    submitBtn.disabled = !confirmPw || confirmPw !== pw;
  }

  newPwInput.addEventListener('input', checkStrength);
  confirmPwInput.addEventListener('input', checkStrength);

  submitBtn.addEventListener('click', () => {
    const newPw = newPwInput.value;
    const confirmPw = confirmPwInput.value;
    if (newPw !== confirmPw) {
      statusEl.textContent = '两次密码不一致';
      statusEl.className = 'fc-status error';
      return;
    }
    submitBtn.disabled = true;
    statusEl.textContent = '正在修改...';
    statusEl.className = 'fc-status';
    setPendingPasswordChangeValue(newPw);
    send({ type: 'change_password', newPassword: newPw });
  });

  newPwInput.focus();
}

export function hideForceChangePassword() {
  const overlay = document.getElementById('force-change-overlay');
  if (overlay) overlay.remove();
}

function clientValidatePassword(pw) {
  if (!pw || pw.length < 8) {
    return { valid: false, message: '密码长度至少 8 位' };
  }
  let types = 0;
  if (/[a-z]/.test(pw)) types++;
  if (/[A-Z]/.test(pw)) types++;
  if (/[0-9]/.test(pw)) types++;
  if (/[^a-zA-Z0-9]/.test(pw)) types++;
  if (types < 2) {
    return { valid: false, message: '需包含至少 2 种字符类型（大写/小写/数字/特殊字符）' };
  }
  return { valid: true, message: '' };
}

// --- Password Changed Handler ---
export function handlePasswordChanged(msg) {
  if (msg.success) {
    const pendingPasswordChangeValue = getPendingPasswordChangeValue();
    const rememberPw = getRememberPw();
    if (pendingPasswordChangeValue !== null) {
      if (rememberPw?.checked || hasRememberedPassword()) {
        saveRememberedPassword(pendingPasswordChangeValue);
        if (rememberPw) rememberPw.checked = true;
      }
      setPendingPasswordChangeValue(null);
    }
    // Update token
    if (_setAuthToken) _setAuthToken(msg.token);
    localStorage.setItem('webcoding-token', msg.token);
    // If force-change overlay is open, close it and load sessions
    const fcOverlay = document.getElementById('force-change-overlay');
    if (fcOverlay) {
      hideForceChangePassword();
      _restoreInitialSession?.(getSelectedAgentValue());
      showToast('密码修改成功');
    }

    // If settings panel change password
    if (_onPasswordChanged) {
      _onPasswordChanged({ success: true, message: msg.message });
      _onPasswordChanged = null;
    }
  } else {
    // Force-change error
    const fcStatus = document.querySelector('#fc-status');
    if (fcStatus) {
      fcStatus.textContent = msg.message || '修改失败';
      fcStatus.className = 'fc-status error';
      const btn = document.querySelector('#fc-submit-btn');
      if (btn) btn.disabled = false;
    }

    // Settings panel error
    if (_onPasswordChanged) {
      _onPasswordChanged({ success: false, message: msg.message });
      _onPasswordChanged = null;
    }
  }
}

export function handleForcedLogout(msg) {
  if (_setAuthToken) _setAuthToken(null);
  localStorage.removeItem('webcoding-token');
  document.dispatchEvent(new CustomEvent('webcoding-auth-failed'));
  if (_getIsGenerating?.()) _finishGenerating?.();
  hideForceChangePassword();
  const loginOverlay = getLoginOverlayEl();
  const app = getAppEl();
  const loginError = getLoginErrorEl();
  if (loginOverlay) loginOverlay.hidden = false;
  if (app) app.hidden = true;
  restoreRememberedPasswordInput();
  if (loginError) {
    loginError.textContent = msg?.message || '登录状态已失效，请重新登录';
    loginError.hidden = false;
  }
}

// --- New Session Modal ---

function buildNewSessionModalHtml() {
  return `
    <div class="modal-panel modal-panel-wide modal-panel-project">
      <div class="modal-header">
        <span class="modal-title">新建 / 打开项目</span>
        <button class="modal-close-btn" id="ns-close-btn">\u2715</button>
      </div>
      <div class="modal-body modal-body-project">
        <div class="modal-stack project-flow-shell" id="ns-step-projects" style="display:none">
          <div class="project-picker-head">
            <div class="project-picker-head-copy">
              <label class="modal-field-label">已保存项目</label>
              <div class="project-picker-summary" id="ns-project-summary">这里展示你已经保存的工作目录。</div>
            </div>
            <div class="project-picker-total" id="ns-project-count">0 个项目</div>
          </div>
          <div class="project-picker-list" id="ns-project-list"></div>
          <div class="project-picker-actions">
            <button class="project-picker-action-btn primary" id="ns-open-dir-btn">打开其他目录</button>
          </div>
          <div class="project-picker-helper">点击任意项目会直接创建新会话；选择其他目录时，也会自动加入左侧项目列表。</div>
        </div>

        <div class="modal-stack dir-browser-shell" id="ns-step-browser">
          <div class="dir-browser-current">
            <div class="dir-browser-current-label">当前目录</div>
            <div class="dir-browser-current-name" id="ns-current-name">正在定位…</div>
            <div class="dir-browser-current-path" id="ns-current-path">载入后会在这里显示你当前选中的完整路径。</div>
          </div>
          <div>
            <label class="modal-field-label">工作目录</label>
            <div class="dir-browser-pathbar" id="ns-pathbar">
              <div class="dir-browser-crumbs" id="ns-crumbs"></div>
              <button class="dir-browser-edit-btn" id="ns-edit-path-btn" title="直接输入路径">\u270E</button>
            </div>
            <div class="dir-browser-manual" id="ns-manual-row" style="display:none">
              <input type="text" id="ns-manual-input" class="modal-text-input" placeholder="输入绝对路径后回车">
              <button class="dir-browser-go-btn" id="ns-manual-go">前往</button>
            </div>
            <div class="dir-browser-list" id="ns-dir-list">
              <div class="dir-browser-empty">正在加载…</div>
            </div>
            <div class="dir-browser-toolbar">
              <label class="dir-browser-toggle">
                <input type="checkbox" id="ns-show-hidden">
                <span>显示隐藏目录</span>
              </label>
              <div class="dir-browser-selection-hint" id="ns-selection-hint">选中后会直接使用当前目录。</div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="modal-btn-secondary" id="ns-back-btn" style="display:none">返回</button>
        <button class="modal-btn-secondary" id="ns-cancel-btn">取消</button>
        <button class="modal-btn-primary" id="ns-select-btn">选择此目录</button>
      </div>
    </div>
  `;
}

function createProjectPickerItemElement(project, sessionCount, onClick) {
  const card = document.createElement('button');
  card.type = 'button';
  card.className = 'project-picker-item';
  card.innerHTML = `
    <span class="project-picker-item-top">
      <span class="project-picker-item-tag">项目</span>
      <span class="project-picker-item-count">${sessionCount > 0 ? `${sessionCount} 个会话` : '暂无会话'}</span>
    </span>
    <span class="project-picker-item-name">${escapeHtml(project.name)}</span>
    <span class="project-picker-item-path">${escapeHtml(project.path)}</span>
    <span class="project-picker-item-note">点击后会立刻进入这个项目，并创建一个新会话。</span>
    <span class="project-picker-item-arrow" aria-hidden="true">\u203A</span>
  `;
  card.addEventListener('click', onClick);
  return card;
}

function createDirBrowserItemElement(name, note, onClick, extraClass = '') {
  const item = document.createElement('button');
  item.type = 'button';
  item.className = `dir-browser-item${extraClass ? ` ${extraClass}` : ''}`;

  const icon = document.createElement('span');
  icon.className = 'dir-browser-item-icon';

  const copy = document.createElement('span');
  copy.className = 'dir-browser-item-copy';

  const nameSpan = document.createElement('span');
  nameSpan.className = 'dir-browser-item-name';
  nameSpan.textContent = name;

  const noteSpan = document.createElement('span');
  noteSpan.className = 'dir-browser-item-note';
  noteSpan.textContent = note;

  const arrow = document.createElement('span');
  arrow.className = 'dir-browser-item-arrow';
  arrow.textContent = '\u203A';

  copy.appendChild(nameSpan);
  copy.appendChild(noteSpan);
  item.appendChild(icon);
  item.appendChild(copy);
  item.appendChild(arrow);
  item.addEventListener('click', onClick);
  return item;
}

function createDirBrowserMessageElement(className, text) {
  const div = document.createElement('div');
  div.className = className;
  div.textContent = text;
  return div;
}

function setManualPathMode(enabled, manualRow, pathbar, manualInput, currentBrowsePath) {
  manualRow.style.display = enabled ? 'flex' : 'none';
  pathbar.style.display = enabled ? 'none' : 'flex';
  if (enabled) {
    manualInput.value = currentBrowsePath || '';
    manualInput.focus();
  }
}

function updateNewSessionBrowserChrome(selectionHintEl, selectBtn) {
  selectionHintEl.textContent = '确认后会把这个目录加入左侧项目列表，并立刻创建新会话。';
  selectBtn.textContent = '打开这个目录';
}

function updateNewSessionPathCard(currentNameEl, currentPathEl, pathValue, hasError = false) {
  if (!pathValue) {
    currentNameEl.textContent = '正在定位…';
    currentPathEl.textContent = '载入后会在这里显示你当前选中的完整路径。';
    return;
  }
  currentNameEl.textContent = getPathLeaf(pathValue);
  currentPathEl.textContent = hasError
    ? `${pathValue}（当前不可用，请换一个目录）`
    : pathValue;
}

function switchNewSessionModalStep({
  stepProjects,
  stepBrowser,
  selectBtn,
  backBtn,
  showBrowser,
  canGoBack = false,
}) {
  stepProjects.style.display = showBrowser ? 'none' : '';
  stepBrowser.style.display = showBrowser ? '' : 'none';
  selectBtn.style.display = showBrowser ? '' : 'none';
  backBtn.style.display = showBrowser && canGoBack ? '' : 'none';
}

function renderNewSessionProjectPickerList({
  projectListEl,
  projectSummaryEl,
  projectCountEl,
  projectsList,
  getSessionCount,
  onProjectSelect,
}) {
  projectListEl.innerHTML = '';
  projectCountEl.textContent = `${projectsList.length} 个项目`;
  projectSummaryEl.textContent = projectsList.length > 0
    ? '点击任意项目会直接创建新会话。'
    : '还没有保存项目，先选一个目录开始。';

  for (const project of projectsList) {
    const card = createProjectPickerItemElement(project, getSessionCount(project), () => onProjectSelect(project));
    projectListEl.appendChild(card);
  }
}

function renderNewSessionCrumbs(crumbsEl, fullPath, onNavigate) {
  crumbsEl.innerHTML = '';
  if (!fullPath) return;
  const parts = fullPath.split('/').filter(Boolean);
  const rootSpan = document.createElement(parts.length > 0 ? 'button' : 'span');
  if (parts.length > 0) rootSpan.type = 'button';
  rootSpan.className = parts.length > 0 ? 'dir-browser-crumb' : 'dir-browser-crumb-current';
  rootSpan.textContent = '/';
  if (parts.length > 0) {
    rootSpan.addEventListener('click', () => onNavigate('/'));
  }
  crumbsEl.appendChild(rootSpan);

  parts.forEach((seg, i) => {
    const sep = document.createElement('span');
    sep.className = 'dir-browser-crumb-sep';
    sep.textContent = '\u203A';
    crumbsEl.appendChild(sep);

    const isLast = i === parts.length - 1;
    const span = document.createElement(isLast ? 'span' : 'button');
    if (!isLast) span.type = 'button';
    span.className = isLast ? 'dir-browser-crumb-current' : 'dir-browser-crumb';
    span.textContent = seg;
    if (!isLast) {
      span.addEventListener('click', () => onNavigate('/' + parts.slice(0, i + 1).join('/')));
    }
    crumbsEl.appendChild(span);
  });

  crumbsEl.scrollLeft = crumbsEl.scrollWidth;
}

function renderNewSessionDirList({
  dirListEl,
  dirs,
  parentPath,
  error,
  currentBrowsePath,
  onNavigate,
}) {
  dirListEl.innerHTML = '';
  if (error) {
    const errDiv = createDirBrowserMessageElement('dir-browser-error', error);
    if (parentPath) {
      const backLink = document.createElement('button');
      backLink.type = 'button';
      backLink.className = 'dir-browser-error-back';
      backLink.textContent = '返回上级目录';
      backLink.addEventListener('click', () => onNavigate(parentPath));
      errDiv.appendChild(backLink);
    }
    dirListEl.appendChild(errDiv);
    return;
  }
  if (parentPath) {
    dirListEl.appendChild(createDirBrowserItemElement('..', '返回上一级目录', () => onNavigate(parentPath), 'is-parent'));
  }
  if (!dirs || dirs.length === 0) {
    dirListEl.appendChild(createDirBrowserMessageElement('dir-browser-empty', '此目录下没有子目录'));
    return;
  }
  for (const dirPath of dirs) {
    const item = createDirBrowserItemElement(getPathLeaf(dirPath), '进入这个目录继续浏览', () => {
      onNavigate(dirPath);
    });
    dirListEl.appendChild(item);
  }
}

export function showNewSessionModal() {
  const targetAgent = getSelectedAgentValue();
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'new-session-overlay';

  let currentBrowsePath = null;
  let showHidden = false;
  overlay.innerHTML = buildNewSessionModalHtml();

  document.body.appendChild(overlay);

  const stepProjects = overlay.querySelector('#ns-step-projects');
  const stepBrowser = overlay.querySelector('#ns-step-browser');
  const projectListEl = overlay.querySelector('#ns-project-list');
  const projectSummaryEl = overlay.querySelector('#ns-project-summary');
  const projectCountEl = overlay.querySelector('#ns-project-count');
  const backBtn = overlay.querySelector('#ns-back-btn');
  const selectBtn = overlay.querySelector('#ns-select-btn');
  const crumbsEl = overlay.querySelector('#ns-crumbs');
  const dirListEl = overlay.querySelector('#ns-dir-list');
  const manualRow = overlay.querySelector('#ns-manual-row');
  const manualInput = overlay.querySelector('#ns-manual-input');
  const pathbar = overlay.querySelector('#ns-pathbar');
  const currentNameEl = overlay.querySelector('#ns-current-name');
  const currentPathEl = overlay.querySelector('#ns-current-path');
  const selectionHintEl = overlay.querySelector('#ns-selection-hint');

  // --- Step 1: Render project list ---
  function renderProjectPicker() {
    const projects = getProjectsValue();
    renderNewSessionProjectPickerList({
      projectListEl,
      projectSummaryEl,
      projectCountEl,
      projectsList: projects,
      getSessionCount: (project) => _getCurrentProjectSessionCount?.(project) || 0,
      onProjectSelect: (project) => {
        close();
        send({ type: 'new_session', projectId: project.id, agent: targetAgent, mode: _getSavedModeForAgent?.(targetAgent) || 'yolo' });
      },
    });
    if (projects.length === 0) {
      showBrowserStep(false);
      return;
    }
  }

  // --- Step 2: Directory browser (reused from original) ---
  function showBrowserStep(canGoBack) {
    switchNewSessionModalStep({
      stepProjects,
      stepBrowser,
      selectBtn,
      backBtn,
      showBrowser: true,
      canGoBack,
    });
    selectBtn.disabled = true;
    updateNewSessionBrowserChrome(selectionHintEl, selectBtn);
    updateNewSessionPathCard(currentNameEl, currentPathEl, currentBrowsePath);
    navigateTo(currentBrowsePath);
  }

  function showProjectStep() {
    switchNewSessionModalStep({
      stepProjects,
      stepBrowser,
      selectBtn,
      backBtn,
      showBrowser: false,
    });
  }

  function navigateTo(dirPath) {
    selectBtn.disabled = true;
    updateNewSessionPathCard(currentNameEl, currentPathEl, dirPath || currentBrowsePath);
    dirListEl.innerHTML = '<div class="dir-browser-empty">正在加载…</div>';
    send({ type: 'browse_directory', path: dirPath, showHidden });
  }

  _onDirectoryListing = (msg) => {
    currentBrowsePath = msg.path || '/';
    renderNewSessionCrumbs(crumbsEl, currentBrowsePath, navigateTo);
    renderNewSessionDirList({
      dirListEl,
      dirs: msg.dirs || [],
      parentPath: msg.parent,
      error: msg.error,
      currentBrowsePath,
      onNavigate: navigateTo,
    });
    updateNewSessionPathCard(currentNameEl, currentPathEl, currentBrowsePath, Boolean(msg.error));
    selectBtn.disabled = Boolean(msg.error);
  };

  // Manual path toggle
  let manualMode = false;
  overlay.querySelector('#ns-edit-path-btn').addEventListener('click', () => {
    manualMode = !manualMode;
    setManualPathMode(manualMode, manualRow, pathbar, manualInput, currentBrowsePath);
  });

  function goToManualPath() {
    const val = manualInput.value.trim();
    if (val) {
      manualMode = false;
      setManualPathMode(false, manualRow, pathbar, manualInput, currentBrowsePath);
      navigateTo(val);
    }
  }
  overlay.querySelector('#ns-manual-go').addEventListener('click', goToManualPath);
  manualInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') goToManualPath();
    if (e.key === 'Escape') {
      manualMode = false;
      setManualPathMode(false, manualRow, pathbar, manualInput, currentBrowsePath);
    }
  });

  // Hidden toggle
  overlay.querySelector('#ns-show-hidden').addEventListener('change', (e) => {
    showHidden = e.target.checked;
    navigateTo(currentBrowsePath);
  });

  function close() {
    overlay.remove();
    _onDirectoryListing = null;
  }

  overlay.querySelector('#ns-close-btn').addEventListener('click', close);
  overlay.querySelector('#ns-cancel-btn').addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  // Back button — return to project picker
  backBtn.addEventListener('click', () => {
    showProjectStep();
    renderProjectPicker();
  });

  overlay.querySelector('#ns-open-dir-btn').addEventListener('click', () => {
    showBrowserStep(true);
  });

  // Select button — create session and auto-persist project by cwd
  selectBtn.addEventListener('click', () => {
    const cwd = currentBrowsePath || null;
    if (!cwd) return;
    const normalizedCwd = normalizeComparablePath(cwd);
    const projects = getProjectsValue();
    const existingProject = projects.find((project) => normalizeComparablePath(project?.path) === normalizedCwd) || null;
    const projectId = existingProject?.id || generateUUID();
    close();
    send(existingProject
      ? { type: 'save_project', id: projectId, path: cwd }
      : { type: 'save_project', id: projectId, path: cwd, name: getPathLeaf(cwd) || cwd });
    send({ type: 'new_session', cwd, agent: targetAgent, mode: _getSavedModeForAgent?.(targetAgent) || 'yolo' });
  });

  // Initialize — default directly into directory browser
  showBrowserStep(false);
}

// --- Import Native Session Modal ---
export function showImportSessionModal() {
  if (getSelectedAgentValue() !== 'claude') return;
  const modal = createImportSessionsModal({
    overlayId: 'import-session-overlay',
    title: '导入本地 CLI 会话',
    closeBtnId: 'is-close-btn',
    bodyId: 'is-body',
    agent: 'claude',
    cardTitle: '从 Claude 原生历史导入',
    cardCopy: '读取 ~/.claude/projects/ 下的会话文件，恢复对话文本与工具调用，并保留 Claude 侧续接上下文。',
    loadingText: '正在加载…',
    onClose: () => {
      _onNativeSessions = null;
    },
  });

  _onNativeSessions = (groups) => {
    const body = modal.body;
    if (!body) return;
    if (!groups || groups.length === 0) {
      modal.renderEmpty('未找到本地 CLI 会话');
      return;
    }
    modal.renderBody();
    for (const group of groups) {
      const groupEl = document.createElement('div');
      groupEl.className = 'import-group import-group-collapsed';

      const groupTitle = document.createElement('div');
      groupTitle.className = 'import-group-title import-group-title-toggle';
      groupTitle.textContent = formatClaudeImportGroupPath(group.dir);
      const arrow = document.createElement('span');
      arrow.className = 'import-group-arrow';
      arrow.textContent = '▶';
      groupTitle.prepend(arrow);

      const sessionList = document.createElement('div');
      sessionList.className = 'import-group-sessions';

      let sessionsRendered = false;
      const renderSessions = () => {
        if (sessionsRendered) return;
        sessionsRendered = true;
        for (const sess of group.sessions) {
          const item = document.createElement('div');
          item.className = 'import-item';
          const info = document.createElement('div');
          info.className = 'import-item-info';
          const titleEl = document.createElement('div');
          titleEl.className = 'import-item-title';
          titleEl.textContent = sess.title;
          const meta = document.createElement('div');
          meta.className = 'import-item-meta';
          const cwdText = sess.cwd ? sess.cwd : '';
          const timeText = sess.updatedAt ? timeAgo(sess.updatedAt) : '';
          meta.textContent = [cwdText, timeText].filter(Boolean).join(' · ');
          info.appendChild(titleEl);
          info.appendChild(meta);
          const btn = document.createElement('button');
          btn.className = 'import-item-btn';
          btn.textContent = sess.alreadyImported ? '重新导入' : '导入';
          btn.addEventListener('click', () => {
            if (sess.alreadyImported) {
              if (!confirm('已导入过此会话，重新导入将覆盖已有内容。确认继续？')) return;
            } else {
              if (!confirm('由于 webcoding 与本地 CLI 的逻辑不同，导入会话需要解析后方可展示，导入后将覆盖已有内容。确认继续？')) return;
            }
            modal.close();
            send({ type: 'import_native_session', sessionId: sess.sessionId, projectDir: group.dir });
          });
          item.appendChild(info);
          item.appendChild(btn);
          sessionList.appendChild(item);
        }
      };

      groupTitle.addEventListener('click', () => {
        const collapsed = groupEl.classList.toggle('import-group-collapsed');
        arrow.textContent = collapsed ? '▶' : '▼';
        if (!collapsed) renderSessions();
      });

      groupEl.appendChild(groupTitle);
      groupEl.appendChild(sessionList);
      body.appendChild(groupEl);
    }
  };

  send({ type: 'list_native_sessions' });
}

export function showImportCodexSessionModal() {
  if (getSelectedAgentValue() !== 'codex') return;
  const modal = createImportSessionsModal({
    overlayId: 'import-codex-session-overlay',
    title: '导入本地 Codex 会话',
    closeBtnId: 'ics-close-btn',
    bodyId: 'ics-body',
    agent: 'codex',
    cardTitle: '从 Codex rollout 历史导入',
    cardCopy: '读取 ~/.codex/sessions/ 下的 rollout 文件，恢复用户消息、助手输出、函数调用和 token 统计。',
    loadingText: '正在加载 Codex 本地历史…',
    onClose: () => {
      _onCodexSessions = null;
    },
  });

  _onCodexSessions = (rawItems) => {
    const body = modal.body;
    if (!body) return;
    const groups = normalizeCodexImportGroups(rawItems);
    if (!groups || groups.length === 0) {
      modal.renderEmpty('未找到本地 Codex 会话');
      return;
    }

    modal.renderBody();
    groups.forEach((group) => {
      const groupEl = document.createElement('div');
      groupEl.className = 'import-group import-group-collapsed';

      const groupTitle = document.createElement('div');
      groupTitle.className = 'import-group-title import-group-title-toggle';
      groupTitle.textContent = group.dir ? formatClaudeImportGroupPath(group.dir) : '无工作目录';
      const arrow = document.createElement('span');
      arrow.className = 'import-group-arrow';
      arrow.textContent = '▶';
      groupTitle.prepend(arrow);

      const sessionList = document.createElement('div');
      sessionList.className = 'import-group-sessions';

      let sessionsRendered = false;
      const renderSessions = () => {
        if (sessionsRendered) return;
        sessionsRendered = true;
        group.sessions.forEach((sess) => {
          const item = document.createElement('div');
          item.className = 'import-item';

          const info = document.createElement('div');
          info.className = 'import-item-info';

          const titleEl = document.createElement('div');
          titleEl.className = 'import-item-title';
          titleEl.textContent = sess.title || sess.threadId;

          const meta = document.createElement('div');
          meta.className = 'import-item-meta';
          meta.textContent = [
            sess.source ? `source:${sess.source}` : '',
            sess.updatedAt ? timeAgo(sess.updatedAt) : '',
          ].filter(Boolean).join(' · ');

          const tags = document.createElement('div');
          tags.className = 'import-item-tags';
          if (sess.cliVersion) {
            const ver = document.createElement('span');
            ver.className = 'import-item-tag';
            ver.textContent = `CLI ${sess.cliVersion}`;
            tags.appendChild(ver);
          }
          if (sess.source) {
            const source = document.createElement('span');
            source.className = 'import-item-tag';
            source.textContent = sess.source;
            tags.appendChild(source);
          }

          info.appendChild(titleEl);
          info.appendChild(meta);
          if (tags.children.length > 0) info.appendChild(tags);

          const btn = document.createElement('button');
          btn.className = 'import-item-btn';
          btn.textContent = sess.alreadyImported ? '重新导入' : '导入';
          btn.addEventListener('click', () => {
            const confirmed = sess.alreadyImported
              ? confirm('已导入过此 Codex 会话，重新导入将覆盖已有内容。确认继续？')
              : confirm('将解析本地 Codex rollout 历史并导入当前 Web 视图。确认继续？');
            if (!confirmed) return;
            modal.close();
            send({ type: 'import_codex_session', threadId: sess.threadId, rolloutPath: sess.rolloutPath });
          });

          item.appendChild(info);
          item.appendChild(btn);
          sessionList.appendChild(item);
        });
      };

      groupTitle.addEventListener('click', () => {
        const collapsed = groupEl.classList.toggle('import-group-collapsed');
        arrow.textContent = collapsed ? '▶' : '▼';
        if (!collapsed) renderSessions();
      });

      groupEl.appendChild(groupTitle);
      groupEl.appendChild(sessionList);
      body.appendChild(groupEl);
    });
  };

  send({ type: 'list_codex_sessions' });
}

function normalizeCodexImportGroups(rawItems) {
  if (!Array.isArray(rawItems) || rawItems.length === 0) return [];
  const looksGrouped = rawItems.every((item) => item && Array.isArray(item.sessions));
  if (looksGrouped) return rawItems;

  const groupMap = new Map();
  rawItems.forEach((sess) => {
    if (!sess || typeof sess !== 'object') return;
    const dir = typeof sess.cwd === 'string' ? sess.cwd : '';
    if (!groupMap.has(dir)) groupMap.set(dir, { dir, updatedAt: null, sessions: [] });
    const group = groupMap.get(dir);
    group.sessions.push(sess);
    if (sess.updatedAt && (!group.updatedAt || +new Date(sess.updatedAt) > +new Date(group.updatedAt))) {
      group.updatedAt = sess.updatedAt;
    }
  });

  return Array.from(groupMap.values())
    .map((group) => {
      group.sessions.sort((a, b) => +new Date(b.updatedAt || 0) - +new Date(a.updatedAt || 0));
      return group;
    })
    .sort((a, b) => +new Date(b.updatedAt || 0) - +new Date(a.updatedAt || 0));
}

export function getNativeSessions() {
  return _onNativeSessions;
}

export function getCodexSessions() {
  return _onCodexSessions;
}

export function getCwdSuggestions() {
  return _onCwdSuggestions;
}

export function getDirectoryListing() {
  return _onDirectoryListing;
}
